import Markdown from 'markdown-to-jsx';
import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';

import AssetPreviewDisplay from 'dashboard/apps/InquiryTemplateEditor/ConnectedAssetPreviewDisplay';
import useEnhancedLimitingForAdditional from 'dashboard/apps/InquiryTemplateEditor/hooks/useEnhancedLimitingForAdditional';
import { Asset } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { ComponentSettingImage } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { isInitialStep } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { Feature, InquiryTemplateVersionStepUi } from 'dashboard/models/inquiry-template-version';
import { TextAreaField } from 'design-system/components/FormField';
import { Label, Paragraph } from 'design-system/components/Text';
import {
  UiComponentConfig,
  UiComponentConfigType,
  UiImageComponentConfig,
} from 'lib/interfaces/ui-components';
import { useFormSecurely } from 'lib/security/reactHookForm';

const StyledFormControlLabel = styled(FormControlLabel)`
  display: flex;
  align-items: center;
  margin: 0px;
`;

interface ImageSettingsProps {
  currentStep: InquiryTemplateVersionStepUi;
  editable: boolean;
  componentConfig: UiImageComponentConfig['config'];
  componentType: UiComponentConfigType;
  componentSetting: ComponentSettingImage<UiImageComponentConfig>;
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
  handleImageSelect: (asset: Asset, imageSetting: ComponentSettingImage) => void;
}

const ImageSetting = ({
  editable,
  componentConfig,
  componentSetting,
  componentType,
  currentStep,
  handleUpdateComponentConfig,
  handleImageSelect,
}: ImageSettingsProps) => {
  const {
    'alt-key': altKey,
    'client-key': clientKey,
    'helper-text': helperText,
    label,
  } = componentSetting;

  const { [Feature.Asset]: limitAddRemoteAsset } = useEnhancedLimitingForAdditional();

  const isImageHostedComponentConfig = componentType === UiComponentConfigType.ImageHosted;

  const initialValue = isImageHostedComponentConfig
    ? componentConfig[altKey as keyof UiComponentConfig['config']]
    : undefined;

  const { control, handleSubmit, reset } = useFormSecurely({
    defaultValues: {
      [altKey]: initialValue,
    },
  });

  const handleSubmitForm = handleSubmit((data) => {
    handleUpdateComponentConfig({ [altKey]: data[altKey] });
  });

  useEffect(() => reset({ [altKey]: initialValue }), [altKey, initialValue, reset]);

  return (
    <>
      {isImageHostedComponentConfig && (
        <>
          <StyledFormControlLabel>Alt text</StyledFormControlLabel>
          <Controller
            name='alt'
            control={control}
            render={({ field: { value, onChange } }) => (
              <TextAreaField
                role='input'
                id={`form-section-image-setting__${clientKey}`}
                value={value}
                disabled={!editable}
                onChange={(e) => {
                  onChange(e.target.value);
                  handleSubmitForm();
                }}
                autoFocus
              />
            )}
          />
        </>
      )}
      <Label weight='bold'>
        {label}
        {helperText && (
          <HelpIcon>
            <Paragraph size='sm'>
              <Markdown>{helperText}</Markdown>
            </Paragraph>
          </HelpIcon>
        )}
      </Label>
      <AssetPreviewDisplay
        disabled={!editable}
        assetInfo={componentConfig}
        remoteAssetsDisabled={
          (limitAddRemoteAsset && !isInitialStep(currentStep)) ||
          componentSetting['client-key'] == null
        }
        personaAssetsDisabled={componentSetting['local-image-key'] == null}
        imageSetting={componentSetting}
        handleImageSelect={(asset: Asset) => {
          handleImageSelect(asset, componentSetting);
        }}
        {...(componentSetting.nullable
          ? {
              handleRemoveAsset: () =>
                handleUpdateComponentConfig({ [componentSetting['client-key']]: null }),
            }
          : {})}
      />
    </>
  );
};

export default ImageSetting;
